import { Link, useLocation } from "react-router-dom";
import { 
  LayoutDashboard, 
  Building2, 
  Target, 
  Briefcase, 
  UsersRound 
} from "lucide-react";

export default function Sidebar() {
  const location = useLocation();

  // ==========================================
  // Logged In User Role (DYNAMIC FIX)
  // Fetching the exact department from localStorage
  // ==========================================
  const storedUser = localStorage.getItem("user");
  const user = storedUser ? JSON.parse(storedUser) : null;
  const currentUserRole = user?.department || "guest";

  // ==========================================
  // Role Permissions
  // ==========================================
  const ROLE_PERMISSIONS = {
    sales: { dashboard: true, customers: true, opportunities: true, deals: true, users: false },
    purchase: { dashboard: true, customers: false, opportunities: false, deals: true, users: false },
    finance: { dashboard: true, customers: false, opportunities: false, deals: true, users: false },
    admin: { dashboard: true, customers: true, opportunities: true, deals: true, users: true },
    // Safeguard fallback profile for unmapped departments
    guest: { dashboard: false, customers: false, opportunities: false, deals: false, users: false },
  };

  // Safe lookup: Default to guest profile if role isn't recognized
  const permissions = ROLE_PERMISSIONS[currentUserRole] || ROLE_PERMISSIONS.guest;

  // ==========================================
  // Sidebar Menu Configuration with Color Systems
  // ==========================================
  const menu = [
    {
      icon: LayoutDashboard,
      path: "/dashboard",
      title: "Dashboard",
      visible: permissions.dashboard,
      // Premium Indigo Palette
      activeStyles: "bg-indigo-50 text-indigo-600 border-indigo-100 shadow-indigo-100/50",
      hoverStyles: "text-slate-400 group-hover:text-indigo-500 group-hover:bg-indigo-50/40 group-hover:border-indigo-100/30",
      indicatorColor: "bg-indigo-600",
    },
    {
      icon: Building2,
      path: "/customers",
      title: "Customers DBM",
      visible: permissions.customers,
      // Premium Emerald/Green Palette
      activeStyles: "bg-emerald-50 text-emerald-600 border-emerald-100 shadow-emerald-100/50",
      hoverStyles: "text-slate-400 group-hover:text-emerald-500 group-hover:bg-emerald-50/40 group-hover:border-emerald-100/30",
      indicatorColor: "bg-emerald-600",
    },
    {
      icon: Target,
      path: "/leads",
      title: "Opportunities",
      visible: permissions.opportunities,
      // Premium Amber/Orange Palette
      activeStyles: "bg-amber-50 text-amber-600 border-amber-100 shadow-amber-100/50",
      hoverStyles: "text-slate-400 group-hover:text-amber-500 group-hover:bg-amber-50/40 group-hover:border-amber-100/30",
      indicatorColor: "bg-amber-600",
    },
    {
      icon: Briefcase,
      path: "/deals",
      title: "Orders",
      visible: permissions.deals,
      // Premium Sky Blue Palette (Matches your Brand #007BFF)
      activeStyles: "bg-blue-50 text-blue-600 border-blue-100 shadow-blue-100/50",
      hoverStyles: "text-slate-400 group-hover:text-blue-500 group-hover:bg-blue-50/40 group-hover:border-blue-100/30",
      indicatorColor: "bg-blue-600",
    },
    {
      icon: UsersRound,
      path: "/users",
      title: "Users",
      visible: permissions.users,
      // Premium Purple Palette
      activeStyles: "bg-purple-50 text-purple-600 border-purple-100 shadow-purple-100/50",
      hoverStyles: "text-slate-400 group-hover:text-purple-500 group-hover:bg-purple-50/40 group-hover:border-purple-100/30",
      indicatorColor: "bg-purple-600",
    },
  ];

  return (
    <aside className="fixed top-0 left-0 z-40 w-[60px] h-full bg-white border-r border-slate-200/80 flex flex-col items-center pt-16 space-y-2 antialiased">
      {menu
        .filter((item) => item.visible)
        .map((item, index) => {
          const isActive = location.pathname === item.path;
          const IconComponent = item.icon;

          return (
            <Link
              key={index}
              to={item.path}
              className="group relative flex items-center justify-center w-full py-0.5 focus:outline-none select-none"
            >
              {/* Dynamic Active Indicator Strip mapped to module profile */}
              <div 
                className={`absolute left-0 w-[3px] rounded-r-full transition-all duration-300 origin-center ${item.indicatorColor} ${
                  isActive ? "h-6 scale-y-100 opacity-100" : "h-2 scale-y-0 opacity-0"
                }`} 
              />

              {/* Multi-Colored Geometric Icon Pod Container */}
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center border transition-all duration-200 ease-in-out ${
                  isActive ? `${item.activeStyles} shadow-sm` : `${item.hoverStyles} border-transparent`
                }`}
              >
                <IconComponent 
                  size={18} 
                  className={`transition-all duration-200 ${
                    isActive ? "stroke-[2.25] scale-[1.02]" : "stroke-[1.75] group-hover:scale-105"
                  }`} 
                />
              </div>

              {/* Clean Tooltip System */}
              <div
                className="
                  absolute left-14 ml-3
                  px-2.5 py-1.5
                  bg-slate-950 text-slate-100
                  text-[11px] font-semibold tracking-wide
                  rounded-lg shadow-xl shadow-slate-900/10
                  border border-slate-800/80
                  whitespace-nowrap
                  opacity-0
                  -translate-x-1.5
                  pointer-events-none
                  transition-all
                  duration-200
                  ease-out
                  group-hover:opacity-100
                  group-hover:translate-x-0
                  z-50
                "
              >
                {item.title}
                <div className="absolute top-1/2 -left-[4px] -translate-y-1/2 border-y-[4px] border-y-transparent border-r-[4px] border-r-slate-950" />
              </div>
            </Link>
          );
        })}
    </aside>
  );
}